/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: The Promise<any> used here must be replaced with actual response models.
import {useMemo} from 'react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useQuery, useMutation, UseQueryOptions, UseQueryResult} from '@tanstack/react-query'
import {findSiteAndLocaleByAlias} from '_utils'
import {getSites} from '@salesforce/retail-react-app/app/utils/site-utils'
import fetch from 'node-fetch'
import type {Menu, LocaleDataResponse, DataAction, NewsletterFormValues, CustomerProductList} from '_types'

interface APIConfig {
    host?: string
    siteId: string
    locale?: string
    isProduction: boolean
    env: string
}

interface SetLocaleArgs {
    locale: string
    dataAction: DataAction
}

/**
 * @name API
 *
 * @description
 * Class that provides methods to interact with the Salesforce Commerce Cloud API.
 *
 * @param {APIConfig} config - Configuration object for the API.
 * @param {string} config.host - The host URL.
 * @param {string} config.siteId - The site ID.
 * @param {boolean} config.isProduction - Flag indicating if the environment is production.
 * @param {string} config.env - The environment ID.
 */
export class API {
    host?: string
    siteId: string
    isProduction: boolean
    env: string

    constructor({host, siteId, isProduction, env}: APIConfig) {
        this.host = host
        this.siteId = siteId
        this.isProduction = isProduction
        this.env = env
    }

    /**
     * @description
     * Builds the request body with necessary parameters.
     *
     * @param {Record<string, string>} params - The request parameters.
     * @returns {Record<string, string>} The request body.
     */
    _buildBody(params: Record<string, string>) {
        const instanceType_prd = 'prd'
        const instanceType_sbx = 'sbx'
        const body = {...params}
        if (this.siteId) {
            body.realm = this.siteId.split('-')[0]
        }
        body.instanceType = this.isProduction ? instanceType_prd : instanceType_sbx
        return body
    }

    /**
     * @description
     * Generic method to interact with the API.
     *
     * @param {string} endpoint - The API endpoint.
     * @param {string} method - The HTTP method.
     * @param {Record<string, string> | null} body - The request body.
     * @param {string} type - The API type.
     * @param {string} version - The API version.
     * @param {string} header - The request header.
     * @param {string} token - The authentication token.
     * @returns {Promise<any>} The API response.
     */
    async siteCustomApi(
        endpoint: string,
        method: string,
        body: Record<string, string> | null,
        type: string,
        version: string,
        header: string,
        token: string,
    ): Promise<any> {
        const headers = {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
        }
        if (body && !endpoint.includes('shopper-context')) {
            body = this._buildBody(body)
        }
        const response = await fetch(`${getAppOrigin()}/mobify/proxy/api${type}/${version}/${endpoint}`, {
            method,
            headers,
            ...(body && {body: JSON.stringify(body)}),
        })
        const geolocationHeader = response?.headers?.get('x-geolocation')?.split(';')
        if (response.status === 204) {
            return {}
        }
        if (!response?.ok || response?.status === 500) {
            return {status: response?.status || 500, errMessage: response?.statusText || 'Internal Server Error'}
        }
        if (endpoint.includes('shopper-context') && geolocationHeader) {
            return geolocationHeader
        }

        return await response.json()
    }

    /**
     * @description
     * Fetches custom preferences.
     *
     * @param {string} sitePref - The site preference.
     * @param {string} token - The authentication token.
     * @returns {Promise<any>} The custom preference response.
     */
    async getCustomPreference(sitePref: string, token: string) {
        const endpoint = `organizations/${this.env}/getvalue?siteId=${this.siteId}&c_pref=${sitePref}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/custom-preferences', 'v1', '', token)
    }

    /**
     * @description
     * Fetches custom objects.
     *
     * @param {string} objectType - The object type.
     * @param {string} objectKey - The object key.
     * @param {string} token - The authentication token.
     * @returns {Promise<any>} The custom object response.
     */
    async getCustomObject(objectType: string, objectKey: string, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/custom-objects/${objectType}/${objectKey}?siteId=${this.siteId}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom-object/shopper-custom-objects', 'v1', '', token)
    }

    /**
     * @description
     * Fetches menu objects.
     *
     * @param {string} level - The menu level.
     * @param {string} locale - The locale.
     * @param {string} token - The authentication token.
     * @returns {Promise<Menu>} The menu object response.
     */
    async getMenuObject(level: string, locale: string, token: string): Promise<Menu> {
        const endpoint = `organizations/${this.env}/menu?siteId=${this.siteId}&locale=${locale}&c_level=${level}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/menu', 'v1', '', token)
    }

    /**
     * @description
     * Fetches locales data.
     *
     * @param {string} localeId - The locale ID.
     * @param {string} token - The authentication token.
     * @returns {Promise<LocaleDataResponse>} The locales data response.
     */
    async getLocales(localeId: string, token: string): Promise<LocaleDataResponse> {
        const endpoint = `organizations/${this.env}/getlocale?siteId=${this.siteId}&locale=${localeId}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/change-locale', 'v1', '', token)
    }

    /**
     * @description
     * Sets the locale.
     *
     * @param {string} site - The site ID.
     * @param {string} localeId - The locale ID.
     * @param {string | undefined | null} action - The action.
     * @param {string} queryString - The query string.
     * @param {string} token - The authentication token.
     * @returns {Promise<any>} The set locale response.
     */
    async setLocale(site: string, localeId: string, action: string | undefined | null, queryString: string, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/setlocale?siteId=${site}&c_action=${action}&c_query=${queryString}&locale=${localeId}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/change-locale', 'v1', '', token)
    }

    /**
     * @description
     * Handles login flow.
     *
     * @param {string} locale - The locale.
     * @param {string} token - The authentication token.
     * @param {object} body - The request body.
     * @param {string} body.basicAuthHeader - The basic authentication header.
     * @returns {Promise<any>} The login flow response.
     */
    async loginFlow(locale: string, token: string, body: {basicAuthHeader: string}): Promise<any> {
        const endpoint = `organizations/${this.env}/loginFlow?siteId=${this.siteId}&locale=${locale}`
        return this.siteCustomApi(endpoint, 'POST', body, '/custom/auth', 'v1', '', token)
    }

    /**
     * @description
     * Fetches geolocation data.
     *
     * @param {string | null} usid - The user session ID.
     * @param {string} token - The authentication token.
     * @param {object} body - The request body.
     * @param {string} body.clientIp - The client IP address.
     * @returns {Promise<any>} The geolocation response.
     */
    async getGeolocation(usid: string | null, token: string, body: {clientIp: string}): Promise<any> {
        const endpoint = `organizations/${this.env}/shopper-context/${usid}?siteId=${this.siteId}`
        return this.siteCustomApi(endpoint, 'PUT', body, '/shopper/shopper-context', 'v1', '', token)
    }

    /**
     * @description
     * Verifies geolocation.
     *
     * @param {string} locale - The locale.
     * @param {string} countryCode - The country code.
     * @param {string} token - The authentication token.
     * @returns {Promise<any>} The verify geolocation response.
     */
    async verifyGeolocation(locale: string, countryCode: string, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/verifyCountry?siteId=${this.siteId}&locale=${locale}&c_countryCode=${countryCode}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/geolocation', 'v1', '', token)
    }

    /**
     * @description
     * Sets the email for the newsletter subscription using the site custom API.
     *
     * @param {string} locale - The locale for the request (e.g., 'en-GB', 'fr-FR').
     * @param {Object} body - The request body containing email and tokenRecaptcha.
     * @param {string} body.email - The email address to subscribe.
     * @param {string} body.tokenRecaptcha - The reCAPTCHA token for verification.
     * @param {string} token - The authentication token for the API request.
     * @returns {Promise<any>} - Newsletter set email response.
     */
    async newsletterModalSetEmail(locale: string, body: {email: string; tokenRecaptcha: string}, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/SubscribeNewsLetterStep1?siteId=${this.siteId}&locale=${locale}`
        return this.siteCustomApi(endpoint, 'POST', body, '/custom/newsletter', 'v1', '', token)
    }

    /**
     * @description
     * Sets the preferences for the newsletter subscription using the site custom API.
     *
     * @param {string} locale - The locale for the request (e.g., 'en-GB', 'fr-FR').
     * @param {Object} body - The request body containing form data.
     * @param {string} body.form - The form data for preferences.
     * @param {string} token - The authentication token for the API request.
     * @returns {Promise<any>} Newsletter set preferences response.
     */
    async newsletterModalSetPreferences(locale: string, body: {form: string}, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/SubscribeNewsLetterStep2?siteId=${this.siteId}&locale=${locale}`
        return this.siteCustomApi(endpoint, 'POST', body, '/custom/newsletter', 'v1', '', token)
    }

    /**
     * @description
     * Resets the password using the site custom API.
     *
     * @param {string} locale - The locale for the request (e.g., 'en-GB', 'fr-FR').
     * @param {string} token - The authentication token for the API request.
     * @param {Object} body - The request body containing login email.
     * @param {string} body.loginEmail - The email address for the login.
     * @returns {Promise<any>} Reset password response.
     */
    async resetPassword(locale: string, token: string, body: {loginEmail: string}): Promise<any> {
        const endpoint = `organizations/${this.env}/resetpassword?siteId=${this.siteId}&locale=${locale}`
        return this.siteCustomApi(endpoint, 'POST', body, '/custom/reset-password', 'v1', '', token)
    }

    /**
     * @description
     * Calculates taxes using Avalara API.
     *
     * @param {string} locale - The locale for the request (e.g., 'en-GB', 'fr-FR').
     * @param {string} basketId - The unique identifier for the basket.
     * @param {string} token - The authentication token for the API request.
     * @returns {Promise<any>} - Calculate taxes response.
     */
    async calculateTaxesAvalara(locale: string, basketId: string, applyTaxesToBasket: boolean, token: string): Promise<any> {
        const endpoint = `organizations/${this.env}/calculateTaxes?siteId=${this.siteId}&locale=${locale}&c_basketId=${basketId}&c_applyTaxesToBasket=${applyTaxesToBasket}`
        return this.siteCustomApi(endpoint, 'POST', {}, '/custom/avalara', 'v1', '', token)
    }

    /**
     * @description
     * Places an order.
     *
     * @param {string} locale - The locale for the order (e.g., 'en-GB', 'fr-FR').
     * @param {string} token - The authentication token for the API request.
     * @param {string} orderId - The unique identifier for the order.
     * @returns {Promise<any>} - Place order response.
     */
    async placeOrder(locale: string, token: string, orderId: string): Promise<any> {
        const endpoint = `organizations/${this.env}/placeOrder?siteId=${this.siteId}&locale=${locale}&c_orderId=${orderId}`
        return this.siteCustomApi(endpoint, 'POST', null, '/custom/order', 'v1', '', token)
    }

    /**
     * @description
     * Retrieves public wishlist.
     *
     * @param {string} locale - The locale for the order (e.g., 'en-GB', 'fr-FR').
     * @param {string} token - The authentication token for the API request.
     * @param {string} wishlistId - Wishlist ID.
     * @returns {Promise<CustomerProductList>} - Public wishlist.
     */
    async getPublicWishlist(locale: string, token: string, wishlistId: string): Promise<CustomerProductList> {
        const endpoint = `organizations/${this.env}/sharewishlist?c_wishlistId=${wishlistId}&locale=${locale}&siteId=${this.siteId}`
        return this.siteCustomApi(endpoint, 'GET', null, '/custom/wishlist', 'v1', '', token)
    }

    /**
     * @description
     * fail order and reopen basket.
     *
     * @param {string} locale - The locale for the order (e.g., 'en-GB', 'fr-FR').
     * @param {string} token - The authentication token for the API request.
     * @param {string} orderId - The unique identifier for the order.
     * @returns {Promise<any>} - Place order response.
     */
    async failOrder(locale: string, token: string, orderId: string): Promise<any> {
        const endpoint = `organizations/${this.env}/failOrder?siteId=${this.siteId}&locale=${locale}&c_orderId=${orderId}`
        return this.siteCustomApi(endpoint, 'POST', null, '/custom/order', 'v1', '', token)
    }
}

/**
 * @name useCustomApi
 *
 * @hook
 *
 * @description
 * Custom hook that provides functions to fetch custom preferences and custom objects from the Salesforce Commerce Cloud API.
 *
 * @returns {Object} obj - The result object.
 * @returns {function} obj.useCustomPreference - A function to fetch a custom preference or multiple custom preferences.
 * @returns {function} obj.useCustomObject - A function to fetch a custom object.
 * @returns {function} obj.useMenuObject - A function to fetch a menu object.
 * @returns {function} obj.useRetrieveLocales - A function to fetch locale data.
 * @returns {function} obj.useSetLocale - A function to set a locale.
 * @returns {function} obj.useLoginFlow - A function to handle login flow.
 * @returns {function} obj.useVerifyGeolocation - A function to verify geolocation.
 * @returns {function} obj.useVerifyNewsletterEmail - A function to verify newsletter email.
 * @returns {function} obj.useSubmitNewsletterPreferences - A function to submit newsletter preferences.
 * @returns {function} obj.useResetPassword - A function to reset password.
 * @returns {function} obj.useCalculateTaxesAvalara - A function to calculate taxes using Avalara.
 * @returns {function} obj.usePlaceOrder - A function to place an order.
 */
const useCustomApi = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {app, customParams} = getConfig()
    const siteId = app.commerceAPI.parameters.siteId
    const isProduction = customParams.isProduction
    const env = app.commerceAPI.parameters.organizationId

    const siteApi = useMemo(
        () =>
            new API({
                env,
                siteId,
                isProduction,
            }),
        [env, siteId, isProduction],
    )

    const useCustomPreference = (sitePref: string | string[]) => {
        const queryKey = ['customPreference', sitePref]
        return useQuery(queryKey, async () => {
            try {
                const token = await getTokenWhenReady()
                if (typeof sitePref === 'string') {
                    const response = await siteApi.getCustomPreference(sitePref, token)
                    return response?.[sitePref]
                } else {
                    const response = await siteApi.getCustomPreference(sitePref.join(','), token)
                    return response
                }
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useCustomObject = (objectType: string, objectKey: string) => {
        const queryKey = ['customObject', objectType, objectKey]
        return useQuery(queryKey, async () => {
            try {
                const token = await getTokenWhenReady()
                const response = await siteApi.getCustomObject(objectType, objectKey, token)
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useMenuObject = (level: string, locale: string) => {
        const queryKey = ['level', level]
        return useQuery(queryKey, async () => {
            try {
                const token = await getTokenWhenReady()
                const response = await siteApi.getMenuObject(level, locale, token)
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useRetrieveLocales = () => {
        const queryKey = ['countries']
        const {locale} = useMultiSite()
        const localeId = locale.id

        return useQuery(queryKey, async () => {
            try {
                const token = await getTokenWhenReady()
                const getLocaleResponse = await siteApi.getLocales(localeId, token)
                return getLocaleResponse
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useLoginFlow = (locale: string) => {
        return useMutation(async ({phone, password, unloggedWishlistID}: {phone: string; password: string; unloggedWishlistID: string}) => {
            try {
                const token = await getTokenWhenReady()
                const basicAuthHeader = 'Basic ' + btoa(`${phone}:${password}`)
                const body = {
                    basicAuthHeader,
                    unloggedWishlistID,
                }
                return await siteApi.loginFlow(locale, token, body)
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useSetLocale = () => {
        return useMutation(async ({locale, dataAction}: SetLocaleArgs) => {
            const action = dataAction.action
            const queryParams = dataAction.queryParams ?? ''
            const queryString = encodeURIComponent(queryParams)

            const {site} = findSiteAndLocaleByAlias(getSites(), locale.toLowerCase())

            if (action && action.length > 0) {
                try {
                    const token = await getTokenWhenReady()
                    const setLocaleResponse = await siteApi.setLocale(site.id, locale, action, queryString, token)
                    return setLocaleResponse
                } catch (err) {
                    console.error(err)
                    throw err
                }
            } else {
                return {success: false, redirectUrl: ''}
            }
        })
    }

    const useVerifyGeolocation = (locale: string, usid: string | null, clientIp: string | undefined = '') => {
        return useQuery(
            [],
            async () => {
                try {
                    const token = await getTokenWhenReady()
                    const body = {
                        clientIp,
                    }
                    const geolocationResponse = await siteApi.getGeolocation(usid, token, body)

                    if (geolocationResponse && geolocationResponse.length > 0) {
                        const geolocationCountry = geolocationResponse
                            ?.find((item) => item.trim().startsWith('CountryCode:'))
                            ?.split(':')[1]
                            ?.trim()

                        const response = await siteApi.verifyGeolocation(locale, geolocationCountry, token)
                        return response
                    } else {
                        return {success: false}
                    }
                } catch (err) {
                    console.error(err)
                    throw err
                }
            },
            {enabled: Boolean(clientIp)},
        )
    }

    const useVerifyNewsletterEmail = () => {
        const {locale} = useMultiSite()
        const localeId = locale.id
        return useMutation(async ({email, tokenRecaptcha, randstr}: {email: string; tokenRecaptcha: string; randstr?: string}) => {
            try {
                const token = await getTokenWhenReady()
                const body = {
                    email: email,
                    tokenRecaptcha: tokenRecaptcha,
                    randstr: randstr,
                }
                const response = await siteApi.newsletterModalSetEmail(localeId, body, token)
                if (response && !response.success) {
                    throw new Error(response.errMessage, response)
                }
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useSubmitNewsletterPreferences = () => {
        const {locale} = useMultiSite()
        const localeId = locale.id
        return useMutation(async ({form}: {form: NewsletterFormValues}) => {
            try {
                const token = await getTokenWhenReady()
                const stringifyForm = JSON.stringify(form)
                const body = {
                    form: stringifyForm,
                }
                const response = await siteApi.newsletterModalSetPreferences(localeId, body, token)
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useResetPassword = (locale: string) => {
        return useMutation(async ({loginEmail}: {loginEmail: string}) => {
            try {
                const token = await getTokenWhenReady()
                const response = await siteApi.resetPassword(locale, token, {loginEmail})
                if (response) {
                    return response
                } else {
                    throw new Error('Invalid response format from loginFlow API')
                }
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const useCalculateTaxesAvalara = () => {
        const {locale} = useMultiSite()

        return useMutation(async ({basketId, applyTaxesToBasket}: {basketId: string; applyTaxesToBasket: boolean}) => {
            try {
                const token = await getTokenWhenReady()
                return await siteApi.calculateTaxesAvalara(locale.id, basketId, applyTaxesToBasket, token)
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const usePlaceOrder = () => {
        return useMutation(async ({locale, orderId}: {locale: string; orderId: string}) => {
            try {
                const token = await getTokenWhenReady()
                const response = await siteApi.placeOrder(locale, token, orderId)
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    const usePublicProductList = (
        locale: string,
        wishlistId: string,
        options?: Omit<UseQueryOptions<CustomerProductList>, 'queryKey' | 'queryFn'>,
    ): UseQueryResult<CustomerProductList> => {
        return useQuery(
            ['publicWishlist', locale, wishlistId],
            async () => {
                try {
                    const token = await getTokenWhenReady()
                    const response = await siteApi.getPublicWishlist(locale, token, wishlistId)
                    return response
                } catch (err) {
                    console.error(err)
                    throw err
                }
            },
            options,
        )
    }

    const useFailOrder = () => {
        return useMutation(async ({locale, orderId}: {locale: string; orderId: string}) => {
            try {
                const token = await getTokenWhenReady()
                const response = await siteApi.failOrder(locale, token, orderId)
                return response
            } catch (err) {
                console.error(err)
                throw err
            }
        })
    }

    return {
        useCustomPreference,
        useCustomObject,
        useMenuObject,
        useRetrieveLocales,
        useSetLocale,
        useLoginFlow,
        useVerifyGeolocation,
        useVerifyNewsletterEmail,
        useSubmitNewsletterPreferences,
        useResetPassword,
        useCalculateTaxesAvalara,
        usePlaceOrder,
        usePublicProductList,
        useFailOrder,
    }
}

export {useCustomApi}
